// src/ui/RightsMeta.jsx — the two things a post says about itself on rights,
// and the form that files a claim about one.
//
// 🟢 THE BROWSER'S HALF OF app/src/components/RightsBadges.tsx and
//    CopyrightNoticeModal.tsx. Same rules underneath (services/contentRights,
//    generated from the phone's TypeScript), same surfaces: the feed card, the
//    image viewer, the reel. gtest checks this table against the app's en.json
//    word for word, for the same reason ReelMeta's safety text is checked —
//    "the same feature on two clients" is mostly a claim about wording.
//
// 🟥 ENGLISH ONLY, DELIBERATELY. The browser has no translation layer; every
//    string in this client is written in place. Keyed identically to en.json so
//    the two can be compared.
import React, { useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  STR, canDispute, mutedReason, showAiBadge, validateNotice,
  DISPUTE_REQUIREMENT_KEY, DISPUTE_STATUS_KEY, DISPUTE_OUTCOME_KEY,
  disputeLabelKey, disputeOpen, disputeTone, disputePayloadOk,
} from '../services/contentRights';
import { disputeMute, fileNotice } from '../services/rightsApi';
/* 🌍 THE ONE LOCALISED CORNER OF THIS CLIENT. The mute explanation and the
   dispute card are, like the moderation outcomes, something a person reads
   about THEIR post — so they come from modStrings' ten-language table, off the
   browser's language. The English row is what RIGHTS_TEXT below exposes, so
   gtest can still hold it against the phone's en.json word for word. */
import { mt } from '../services/modStrings';

export const RIGHTS_TEXT = {
  rights_muted: 'Sound removed — this video contained copyrighted music',
  rights_muted_short: 'Sound removed',
  rights_ai_badge: 'AI',
  rights_ai_long: 'Made with AI',
  rights_ai_toggle: 'Made with AI',
  rights_ai_hint: 'Label this as AI-generated or AI-edited',
  rights_dispute: 'Dispute',
  rights_disputed: "Dispute sent — we'll review it",
  rights_report: 'Report copyright infringement',
  rights_taken_down: 'Removed after a copyright notice',
  rights_notice_intro: 'Use this form only if you own the copyright to something in this post, or are authorised to act for the owner. False claims can carry legal consequences.',
  rights_notice_work: 'The work being infringed',
  rights_notice_work_ph: 'e.g. my song "Blue", released 2024',
  rights_notice_work_url: 'Where the original can be seen (optional)',
  rights_notice_name: 'Your full name',
  rights_notice_email: 'Your email',
  rights_notice_on_behalf: 'You are',
  rights_notice_owner: 'the owner',
  rights_notice_agent: 'an authorised agent',
  rights_notice_good_faith: 'I have a good-faith belief that this use is not authorised by the copyright owner, their agent, or the law.',
  rights_notice_accurate: 'The information in this notice is accurate, and under penalty of perjury I am the owner or authorised to act for the owner.',
  rights_notice_signature: 'Signature (type your full name)',
  rights_notice_signature_ph: 'Your full name, as above',
  rights_notice_signature_mismatch: 'The signature must match the name above.',
  rights_notice_submit: 'Send notice',
  rights_notice_failed: "The notice couldn't be sent. Please try again.",
  rights_notice_sent: 'Notice received',
  rights_notice_sent_sub: "We'll review it and let the author know. You'll hear from us at the email you gave.",
};
/* the keys that live in the localised table rather than in the object above */
const LOCAL_KEYS = [
  STR.mutedTitle, STR.mutedPush, STR.mutedNotif, STR.mutedWhyTitle, STR.mutedWhy, STR.mutedWhyGeneric,
  STR.mutedWhatTitle, STR.mutedWhat, STR.mutedHowTitle, STR.mutedHow, STR.disputeSend, STR.disputeOpenChat,
  STR.disputeCard, STR.disputeAbout, STR.reqTitle, STR.statusLabel,
  ...Object.values(DISPUTE_REQUIREMENT_KEY), ...Object.values(DISPUTE_STATUS_KEY), ...Object.values(DISPUTE_OUTCOME_KEY),
];
for (const k of LOCAL_KEYS) RIGHTS_TEXT[k] = mt(k, 'en');

const T = (k) => RIGHTS_TEXT[k] || k;
/** the localised reading of a key, English when the browser's language has none */
const L = (k, params) => mt(k, undefined, params) || T(k);

/* the pill's colours: amber waiting, blue being reviewed, green/red decided */
export const TONE_COLOR = { waiting: '#E0A100', assigned: '#2F80ED', released: '#2FA84F', upheld: '#D64545' };

/** The dispute's live status — drawn on the reel banner, the sheet and the chat card. */
export function DisputeStatusPill({ dispute, size = 'md' }) {
  const key = disputeLabelKey(dispute);
  if (!key) return null;
  const tone = disputeTone(dispute) || 'waiting';
  const color = TONE_COLOR[tone];
  return (
    <span className={`vb-dstatus ${size} ${disputeOpen(dispute) ? 'live' : ''}`} style={{ color, borderColor: color + '66', background: color + '1A' }} role="status">
      <i style={{ background: color }} />{L(key)}
    </span>
  );
}

/* ── "AI" ──────────────────────────────────────────────────────────────── */
/** The AI label. Renders nothing for a post that has not earned it. */
export function AiBadge({ post, size = 'sm', long = false, className = '' }) {
  if (!showAiBadge(post)) return null;
  return (
    <span className={`vb-ai-badge ${size} ${className}`} title={T(STR.aiBadgeLong)} aria-label={T(STR.aiBadgeLong)}>
      <svg viewBox="0 0 24 24" width={size === 'md' ? 13 : 11} height={size === 'md' ? 13 : 11} fill="currentColor" aria-hidden>
        <path d="M12 2l1.8 5.2L19 9l-5.2 1.8L12 16l-1.8-5.2L5 9l5.2-1.8L12 2zm7 11l.9 2.6L22.5 16.5l-2.6.9L19 20l-.9-2.6-2.6-.9 2.6-.9L19 13zM5 14l.7 2 2 .7-2 .7L5 19.4l-.7-2-2-.7 2-.7L5 14z" />
      </svg>
      {T(long ? STR.aiBadgeLong : STR.aiBadge)}
    </span>
  );
}

/* ── "Sound removed" ───────────────────────────────────────────────────── */
/** The banner on a clip whose sound was removed, with the author's Dispute. */
export function MutedNotice({ post, myUid, className = '', onDisputed, onExplain, onOpenChat }) {
  const [busy, setBusy] = useState(false);
  /* the dispute the server just returned, until the post in hand catches up */
  const [sentDispute, setSentDispute] = useState(null);
  if (!mutedReason(post)) return null;
  const mine = !!myUid && post?.userId === myUid;
  const dispute = post?.audioCheck?.dispute || sentDispute;
  const mayDispute = !sentDispute && canDispute(post, myUid);
  const track = post?.audioCheck?.match;
  const send = async (e) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      const r = await disputeMute(post.id);
      const d = r?.dispute || { at: Date.now(), status: 'waiting', outcome: null };
      setSentDispute(d); onDisputed?.(post.id, d);
      /* the dispute IS a conversation — take them to it */
      onOpenChat?.(r?.chatId || null);
    } catch { /* the banner stays */ }
    setBusy(false);
  };
  const Body = onExplain && mine ? 'button' : 'div';
  return (
    <div className={`vb-muted ${className}`} role="alert" onClick={(e) => e.stopPropagation()}>
      <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M11 5 6 9H2v6h4l5 4V5z" /><path d="m23 9-6 6M17 9l6 6" />
      </svg>
      <Body className="vb-muted-txt" {...(Body === 'button' ? { type: 'button', onClick: (e) => { e.stopPropagation(); onExplain(post); } } : {})}>
        <b>{T(STR.muted)}</b>
        {track?.title && <i>{track.title}{track.artists?.length ? ` — ${track.artists.join(', ')}` : ''}</i>}
        {/* the same live status the chat and the sheet show — one rule, one pill */}
        {mine && dispute && <DisputeStatusPill dispute={dispute} size="sm" />}
      </Body>
      {mayDispute ? (
        <button type="button" className="vb-muted-btn" disabled={busy} onClick={send}>
          {busy ? '…' : T(STR.dispute)}
        </button>
      ) : mine && dispute && onOpenChat ? (
        <button type="button" className="vb-muted-btn" title={L(STR.disputeOpenChat)} aria-label={L(STR.disputeOpenChat)} onClick={(e) => { e.stopPropagation(); onOpenChat(null); }}>💬</button>
      ) : null}
    </div>
  );
}

/* ── the explanation sheet ─────────────────────────────────────────────── */
/**
 * "The sound was removed from your video" — why, what changed, how to fix it,
 * and the dispute. Rises from the bottom to the middle of the screen. Opened
 * from the bell (a `post_muted` notification) or from the reel banner (the
 * live post); subscribes to the post so the status moves while it is open.
 * The twin of app/src/components/MutedExplainSheet.tsx.
 */
export function MutedExplainSheet({ notif, post, myUid, onClose, onDisputed, onOpenChat }) {
  const postId = post?.id || notif?.postId || null;
  const [live, setLive] = useState(post || null);
  const [gone, setGone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => {
    if (!postId) return undefined;
    return onSnapshot(doc(db, 'posts', postId), (snap) => {
      if (!snap.exists()) { setGone(true); return; }
      setLive({ id: snap.id, ...snap.data() });
    }, () => {});
  }, [postId]);

  const track = live?.audioCheck?.match || notif?.track || null;
  const trackLine = track?.title ? `${track.title}${track.artists?.length ? ` — ${track.artists.join(', ')}` : ''}` : '';
  const dispute = live?.audioCheck?.dispute || null;
  const stillMuted = live ? mutedReason(live) === 'copyright' : true;
  const mayDispute = !!live && canDispute(live, myUid);
  const image = live?.media?.find?.((m) => m?.type === 'video')?.thumbnailUrl || notif?.postImageUrl || null;
  const caption = live?.text || notif?.postContent || '';
  const dir = /^ar/i.test(navigator.language || '') ? 'rtl' : 'ltr';

  const send = async () => {
    if (busy || !postId) return;
    setBusy(true); setErr('');
    try {
      const r = await disputeMute(postId);
      if (r?.dispute) { setLive((p) => (p ? { ...p, audioCheck: { ...(p.audioCheck || {}), dispute: r.dispute } } : p)); onDisputed?.(postId, r.dispute); }
      onClose();
      onOpenChat?.(r?.chatId || null);
    } catch { setErr(T('rights_notice_failed')); }
    setBusy(false);
  };

  return (
    <div className="vb-fixed-share vb-mute-wrap" onClick={onClose}>
      <div className="vb-sheet vb-mute-sheet" dir={dir} role="dialog" aria-label={L(STR.mutedTitle)} onClick={(e) => e.stopPropagation()}>
        <div className="vb-sheet-head">
          <b className="vb-mute-title"><span className="vb-mute-ico">🔇</span>{L(STR.mutedTitle)}</b>
          <button className="icon-btn" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="vb-sheet-body vb-mute-body">
          {(image || caption || trackLine) && (
            <div className="vb-mute-post">
              {image ? <img src={image} alt="" /> : <span className="vb-mute-noimg">▶</span>}
              <div>
                {caption && <b>{caption}</b>}
                {trackLine && <i>♪ {trackLine}</i>}
                {gone && <i>{mt('vibe_ui_post_removed')}</i>}
              </div>
            </div>
          )}
          <h4>{L(STR.mutedWhyTitle)}</h4>
          <p>{trackLine ? L(STR.mutedWhy, { track: trackLine }) : L(STR.mutedWhyGeneric)}</p>
          <h4>{L(STR.mutedWhatTitle)}</h4>
          <p>{L(STR.mutedWhat)}</p>
          <h4>{L(STR.mutedHowTitle)}</h4>
          <p>{L(STR.mutedHow)}</p>
          {dispute && (
            <div className="vb-mute-status">
              <span>{L(STR.disputeCard)} · {L(STR.statusLabel)}</span>
              <DisputeStatusPill dispute={dispute} />
            </div>
          )}
          {err && <p style={{ color: TONE_COLOR.upheld }}>{err}</p>}
        </div>
        <div className="vb-mute-foot">
          {dispute || !stillMuted ? (
            <button type="button" className="vb-mute-btn" onClick={() => { onClose(); onOpenChat?.(dispute?.chatId || null); }}>💬 {L(STR.disputeOpenChat)}</button>
          ) : mayDispute ? (
            <button type="button" className="vb-mute-btn" disabled={busy} onClick={send}>{busy ? '…' : L(STR.disputeSend)}</button>
          ) : (
            <button type="button" className="vb-mute-btn ghost" onClick={onClose}>Close</button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── the dispute in the chat ───────────────────────────────────────────── */
/**
 * The author's dispute message in the official Allo chat: a `type: 'text'`
 * message with `supportKind: 'dispute'` — see support.js on why nothing in that
 * thread gets its own type. The status is `supportPayload.status`, rewritten by
 * the server as the ticket moves and delivered by the chat's own listener.
 * The twin of app/src/components/DisputeCard.tsx.
 */
export function DisputeCard({ payload, text, mine, onOpenPost }) {
  if (!disputePayloadOk(payload)) return <span>{text || ''}</span>;
  const p = payload;
  const trackLine = p.track?.title ? `${p.track.title}${p.track.artists?.length ? ` — ${p.track.artists.join(', ')}` : ''}` : '';
  const reqs = Array.isArray(p.requirements) && p.requirements.length ? p.requirements : Object.keys(DISPUTE_REQUIREMENT_KEY);
  return (
    <div className="vb-dcard" aria-label={L(STR.disputeCard)}>
      {/* 🟥 THE SUBJECT WRAPS AND THE REFERENCE IS A CHIP. Run together on one
          line they truncated each other — and the reference is the thing both
          sides quote. Same shape as the phone's card. */}
      <div className="vb-dcard-head">
        <span className="vb-dcard-ico">🛡</span>
        <b>{L(STR.disputeCard)}</b>
        {p.ref ? <em className="vb-dcard-ref">{p.ref}</em> : null}
      </div>
      <i className="vb-dcard-sub">{L(STR.disputeAbout)}</i>
      <button type="button" className="vb-dcard-post" disabled={!onOpenPost} onClick={() => onOpenPost?.(String(p.postId))}>
        {p.postImageUrl ? <img src={p.postImageUrl} alt="" /> : <span className="vb-mute-noimg">▶</span>}
        <div>
          {p.postContent && <b>{p.postContent}</b>}
          {trackLine && <i>♪ {trackLine}</i>}
        </div>
      </button>
      {p.text && <p className="vb-dcard-words">{p.text}</p>}
      <div className="vb-dcard-req-title">{L(STR.reqTitle)}</div>
      <ul className="vb-dcard-reqs">{reqs.map((id) => <li key={id}>{L(DISPUTE_REQUIREMENT_KEY[id] || id)}</li>)}</ul>
      <div className="vb-dcard-status"><span>{L(STR.statusLabel)}</span><DisputeStatusPill dispute={p} size="sm" /></div>
    </div>
  );
}

/** Short marker for the feed card's corner, where the speaker would be. */
export function MutedPill({ post }) {
  if (!mutedReason(post)) return null;
  return <span className="vb-muted-pill" title={T(STR.muted)}>{T(STR.mutedShort)}</span>;
}

/* ── the notice form ───────────────────────────────────────────────────── */
/**
 * Filing a copyright notice about a post. The fields are §512(c)(3)'s and
 * which are required is validateNotice — the same rule the server applies.
 */
export function CopyrightNoticeSheet({ post, me, onClose }) {
  const [work, setWork] = useState('');
  const [workUrl, setWorkUrl] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState(me?.email || '');
  const [onBehalf, setOnBehalf] = useState('owner');
  const [goodFaith, setGoodFaith] = useState(false);
  const [accurate, setAccurate] = useState(false);
  const [signature, setSignature] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);

  const draft = useMemo(() => ({
    postId: post?.id, work, workUrl: workUrl || null, name, email, onBehalf, goodFaith, accurate, signature,
  }), [post?.id, work, workUrl, name, email, onBehalf, goodFaith, accurate, signature]);
  const missing = validateNotice(draft);
  const ready = missing.length === 0 && !busy;

  const submit = async () => {
    if (!ready) return;
    setBusy(true); setErr('');
    try { await fileNotice(draft); setDone(true); }
    catch { setErr(RIGHTS_TEXT.rights_notice_failed); }
    setBusy(false);
  };

  return (
    <div className="vb-overlay" onClick={onClose}>
      <div className="vb-sheet vb-pagemaker vb-rights" onClick={(e) => e.stopPropagation()}>
        <div className="vb-sheet-head">
          <b>{RIGHTS_TEXT.rights_report}</b>
          <button className="icon-btn" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {done ? (
          <div className="vb-sheet-body pm vb-rights-done">
            <b>{RIGHTS_TEXT.rights_notice_sent}</b>
            <p>{RIGHTS_TEXT.rights_notice_sent_sub}</p>
            <button className="lg-go slim" onClick={onClose}>Done</button>
          </div>
        ) : (
          <>
            <div className="vb-sheet-body pm">
              <p className="vb-rights-intro">{RIGHTS_TEXT.rights_notice_intro}</p>
              <div className="pm-group">
                <div className="pm-field">
                  <label htmlFor="rt-work">{RIGHTS_TEXT.rights_notice_work}<u>required</u></label>
                  <textarea id="rt-work" rows={2} value={work} maxLength={2000} placeholder={RIGHTS_TEXT.rights_notice_work_ph} onChange={(e) => setWork(e.target.value)} />
                </div>
                <div className="pm-field">
                  <label htmlFor="rt-url">{RIGHTS_TEXT.rights_notice_work_url}</label>
                  <input id="rt-url" type="url" value={workUrl} placeholder="https://" onChange={(e) => setWorkUrl(e.target.value)} />
                </div>
                <div className="pm-field">
                  <label htmlFor="rt-name">{RIGHTS_TEXT.rights_notice_name}<u>required</u></label>
                  <input id="rt-name" value={name} maxLength={200} onChange={(e) => setName(e.target.value)} />
                </div>
                <div className="pm-field">
                  <label htmlFor="rt-email">{RIGHTS_TEXT.rights_notice_email}<u>required</u></label>
                  <input id="rt-email" type="email" value={email} maxLength={200} onChange={(e) => setEmail(e.target.value)} />
                </div>
                <div className="pm-field">
                  <label>{RIGHTS_TEXT.rights_notice_on_behalf}</label>
                  <div className="pm-chips">
                    {['owner', 'agent'].map((k) => (
                      <button key={k} type="button" className={onBehalf === k ? 'on' : ''} onClick={() => setOnBehalf(k)}>
                        {k === 'owner' ? RIGHTS_TEXT.rights_notice_owner : RIGHTS_TEXT.rights_notice_agent}
                      </button>
                    ))}
                  </div>
                </div>
                <label className="vb-rights-check">
                  <input type="checkbox" checked={goodFaith} onChange={(e) => setGoodFaith(e.target.checked)} />
                  <span>{RIGHTS_TEXT.rights_notice_good_faith}</span>
                </label>
                <label className="vb-rights-check">
                  <input type="checkbox" checked={accurate} onChange={(e) => setAccurate(e.target.checked)} />
                  <span>{RIGHTS_TEXT.rights_notice_accurate}</span>
                </label>
                <div className="pm-field">
                  <label htmlFor="rt-sig">{RIGHTS_TEXT.rights_notice_signature}<u>required</u></label>
                  <input id="rt-sig" value={signature} maxLength={200} placeholder={RIGHTS_TEXT.rights_notice_signature_ph} onChange={(e) => setSignature(e.target.value)} />
                  {signature && missing.includes('signature') && <span className="vb-pm-err">{RIGHTS_TEXT.rights_notice_signature_mismatch}</span>}
                </div>
                {err && <div className="vb-pm-err">{err}</div>}
              </div>
            </div>
            <div className="vb-sheet-foot pm-foot">
              <span className="pm-hint">{missing.length ? `${missing.length} to go` : 'Ready to send'}</span>
              <button className="lg-go" disabled={!ready} onClick={submit}>{busy ? 'Sending…' : RIGHTS_TEXT.rights_notice_submit}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
